<script lang="ts" setup>
  import { type PropType, computed } from 'vue';
  import { useBreakpoints } from '@storefront/storefront';
  import { useHttpClient, useRoute } from '@othercommerce/foundation';
  import type { Pagination } from '@storefront/types';
  import { StoreLayout, AccountLayout } from '@storefront/components';
  import { ReferenceType, type DictionaryRecord } from '@othercommerce/domain';

  interface RelatedDocument {
    documentDate: string,
    documentNumber: string,
    referenceType: ReferenceType,
  }

  interface WarehouseDocument {
    documentType: DictionaryRecord;
    documentNumber: string;
    documentDate: string;
    currencyCode: string;
    currencyExchangeRate: string;
    value: string | null;
    relations: RelatedDocument[];
    download: string | null;
  }

  defineOptions({
    layout: [StoreLayout, AccountLayout],
    inheritAttrs: false,
  });

  const props = defineProps({
    documents: { type: Object as PropType<Pagination<WarehouseDocument>>, required: true },
    from: { type: String, required: false },
    to: { type: String, required: false },
  });

  const breakpoints = useBreakpoints();
  const http = useHttpClient();
  const route = useRoute();

  const initial = computed(() => {
    return {
      from: props.from,
      to: props.to,
    };
  });

  function handleFilters(data: any) {
    return http.get(route('web.account.warehouse.documents.index', data));
  }
</script>
<template>
  <AccountMobileNavigation :href="$route('web.account.show')">
    {{ $t('SHOP.ACCOUNT.HEADER.WAREHOUSE_DOCUMENTS') }}
  </AccountMobileNavigation>
  <SfTile class="account-warehouse-documents" large>
    <template v-if="breakpoints.lg" #title>
      <h1 class="account__heading">
        {{ $t('SHOP.ACCOUNT.HEADER.WAREHOUSE_DOCUMENTS') }}
      </h1>
    </template>
    <template #default>
      <div class="account-warehouse-documents-filters">
        <FormController v-slot="{ submit }" :data="initial" :onSubmit="handleFilters">
          <div class="col">
            <SfFormInput :label="$t('SHOP.ACCOUNT.LABEL.WAREHOUSE_DOCUMENTS_FROM')" name="from" type="date" @change="submit" />
          </div>
          <div class="col">
            <SfFormInput :label="$t('SHOP.ACCOUNT.LABEL.WAREHOUSE_DOCUMENTS_TO')" name="to" type="date" @change="submit" />
          </div>
        </FormController>
      </div>
      <div v-if="documents.data.length > 0" class="account-warehouse-documents__content">
        <SfListCard v-for="document in documents.data" :key="document.documentNumber" class="account-warehouse-documents">
          <template #default>
            <div class="account-warehouse-documents__header">
              <h2 class="account-warehouse-documents__heading">
                {{ document.documentType.label }} {{ document.documentNumber }}
              </h2>
              <p class="account-warehouse-documents__date">
                {{ $t('SHOP.DOCUMENT.LABEL.DATE') }}
                <SfDateTime :value="document.documentDate" />
              </p>
            </div>
            <div class="account-warehouse-documents__details">
              <span v-for="related in document.relations" class="account-warehouse-documents__related">
                <span>
                  <small>{{ related.referenceType === ReferenceType.CORRECTION ? $t('SHOP.DOCUMENT.LABEL.CORRECTION_TO') : $t('SHOP.DOCUMENT.LABEL.RELATED_TO') }}</small>
                </span>&nbsp;
                <span>
                  {{ related.documentNumber }}
                </span>&nbsp;
                <span>
                  <small>{{ $t('SHOP.DOCUMENT.LABEL.DATE') }}</small>
                </span>&nbsp;
                <SfDateTime :value="related.documentDate" />
              </span>
              <span class="account-warehouse-documents__separator" />
              <span class="account-warehouse-documents__price">
                <span>
                  <small>{{ $t('SHOP.DOCUMENT.LABEL.TOTAL_VALUE') }}</small>
                </span>&nbsp;
                <SfMoney v-if="document.value" :code="document.currencyCode" :value="document.value" />
                <span v-else class="account-warehouse-documents__price--no-data">
                  <small>{{ $t('SHOP.DOCUMENT.LABEL.TOTAL_VALUE_MISSING') }}</small>
                </span>
              </span>
            </div>
          </template>
          <template #actions>
            <SfButton :disabled="!document.download" :href="document.download || ''" small target="_blank">
              {{ document.download ? $t('SHOP.ACCOUNT.ACTION.DOCUMENT_DOWNLOAD') : $t('SHOP.ACCOUNT.ACTION.DOCUMENT_UNAVAILABLE') }}
            </SfButton>
          </template>
        </SfListCard>
        <SfPagination :pagination="documents" />
      </div>
      <SfDataPlaceholder v-else>
        {{ $t('SHOP.ACCOUNT.MESSAGE.WAREHOUSE_DOCUMENTS_NO_DATA') }}
      </SfDataPlaceholder>
    </template>
  </SfTile>

  <RouterView />
</template>

<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .account-warehouse-documents-filters {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .account-warehouse-documents {
    .list-card__content-default {
      gap: 0;
    }

    &__content {
      display: flex;
      flex-direction: column;
      row-gap: 1.25rem;
    }

    &__details {
      display: flex;
      flex-direction: column;

      small {
        font-weight: var(--font-weight-regular);
      }
    }

    &__header {
      display: flex;
      align-items: center;
      column-gap: 0.625rem;
    }

    &__heading {
      font-size: 1.25rem;
    }

    &__content {
      display: flex;
      flex-direction: column;
    }

    &__date,
    &__count {
      font-size: 0.875rem;
      color: var(--sf-account-color-dark-grey);
    }

    &__overdue {
      font-weight: var(--font-weight-bold);
      color: var(--danger-color);
    }

    &__separator {
      display: block;
      margin: 0.5rem 0;
      background: var(--secondary-border);
      height: 1px;
      width: 100%;
    }

    &__price {
      font-weight: var(--font-weight-bold);
      margin-top: 0.125rem;

      &--no-data {
        color: var(--secondary-color-disabled);
      }
    }

    &__related {
      font-weight: var(--font-weight-bold);
      font-size: 0.875rem;
      margin-top: 0.125rem;
    }
  }

  @include media.query(lg) {
    .account-warehouse-documents {
      row-gap: 1rem;

      &__content .list-card__actions {
        gap: 0.75rem;
      }
    }
  }
</style>
